import React, { FC, useCallback, useState } from 'react'
import { FormProvider, useForm, useWatch } from 'react-hook-form'

import { yupResolver } from '@hookform/resolvers/yup'
import isEmail from 'validator/lib/isEmail'
import * as yup from 'yup'

import { AccountCreationFormInputs } from 'src/public/components/default_template/online_ordering/account/pwlessAuth/PwlessAuth'
import InputField from 'src/shared/components/common/form_input/InputField'
import { t } from 'src/translations'

import Button from 'shared/components/common/button'
import { alertSuccess } from 'shared/js/alertUtils'

import { profileInformationSchema } from 'public/components/default_template/online_ordering/account/form-helpers'
import { useCustomer } from 'public/components/online_ordering/CustomerContextCommon'
import { InternationalPhoneInput } from 'shared/components/common/form_input/InternationalPhoneInput'
import {
  CheckCircleSelectedIcon,
  CloseCircleIcon
} from '@toasttab/buffet-pui-icons'
import Skeleton from 'react-loading-skeleton'
import { AccountCreationLegalDisclaimer } from 'public/components/default_template/online_ordering/account/pwlessAuth/legalUtils'

type CompleteProfileProps = {
  accountInfo: AccountCreationFormInputs
  checkingOut?: boolean
  onSuccessfulLogin?: () => void
  skipLoginToast?: boolean
  setCanGoBack: (canGoBack: boolean) => void
  saveValuesForCheckout?: (formInputs: CompleteProfileFormInputs) => void
  handleClose: () => void
}

export type CompleteProfileFormInputs = {
  email: string
  firstName: string
  lastName: string
}

const AUTH_REFRESH_DELAY = 3000 // Wait 3 seconds before refreshing auth token and closing modal

export const CompleteProfileForm: FC<CompleteProfileProps> = ({
  accountInfo,
  checkingOut = false,
  onSuccessfulLogin,
  skipLoginToast = false,
  setCanGoBack,
  saveValuesForCheckout,
  handleClose
}) => {
  const { completeSignup } = useCustomer()
  const [submitting, setSubmitting] = useState(false)
  // We don't need to validate the phone field, remove it from the resolver
  const { phone, ...schemaFields } = profileInformationSchema.fields
  const formMethods = useForm<CompleteProfileFormInputs>({
    mode: 'onTouched',
    // @ts-ignore
    resolver: yupResolver(yup.object(schemaFields)),
    defaultValues: {
      email: accountInfo.email,
      firstName: accountInfo.firstName,
      lastName: accountInfo.lastName
    }
  })
  const email = useWatch({
    name: 'email',
    control: formMethods.control,
    defaultValue: accountInfo.email
  })
  const firstName = useWatch({
    name: 'firstName',
    control: formMethods.control,
    defaultValue: accountInfo.firstName
  })
  const lastName = useWatch({
    name: 'lastName',
    control: formMethods.control,
    defaultValue: accountInfo.lastName
  })
  const { handleSubmit, setValue } = formMethods
  const [hasError, setHasError] = useState(false)
  const [succeeded, setSucceeded] = useState(false)
  const onSuccessfulAccountCreation = useCallback(
    async (name?: string | null) => {
      onSuccessfulLogin?.()
      setTimeout(async () => {
        handleClose()
        if (!skipLoginToast) {
          alertSuccess(name ? `Welcome, ${name}!` : 'Welcome!')
        }
      }, AUTH_REFRESH_DELAY + 1000)
    },
    [onSuccessfulLogin, skipLoginToast, handleClose]
  )

  const submit = useCallback(
    async (inputData: CompleteProfileFormInputs) => {
      setSubmitting(true)
      setCanGoBack(false)
      try {
        const success = await completeSignup(
          inputData.email,
          inputData.firstName,
          inputData.lastName,
          AUTH_REFRESH_DELAY
        )
        if (success) {
          setSubmitting(false)
          setSucceeded(true)
          await onSuccessfulAccountCreation(inputData.firstName)
        } else {
          setHasError(true)
          setCanGoBack(true)
        }
      } catch (err) {
        setHasError(true)
        setCanGoBack(true)
      } finally {
        setSubmitting(false)
      }
    },
    [completeSignup, onSuccessfulAccountCreation, setCanGoBack]
  )

  const onBlurInput = (emailValue: string) => () => {
    setValue('email', emailValue, {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: true
    })
  }

  if (submitting || hasError || succeeded) {
    return <SuccessOrError loading={submitting} success={succeeded} />
  }
  return (
    <FormProvider {...formMethods}>
      <div className='headerContent centered'>
        <h2 className='title' id='pwless-auth-modal-header'>
          {t('complete-profile-form.check-out-faster')}
        </h2>
        <div className='subtitle'>{t('complete-profile-form.value-prop')}</div>
      </div>
      {/* Stop propagation of the event so that submitting this form doesn't also trigger a submit of any parent forms, like the checkout form */}
      <form
        className='completeProfileForm'
        onSubmit={(event) => {
          event.stopPropagation()
          handleSubmit(submit)(event)
        }}
      >
        <div className='formBody'>
          {hasError ? (
            <span className='formError'>
              {t('complete-profile-form.information-invalid-try-again')}
            </span>
          ) : null}
          <div className='inputs'>
            <div className='nameContainer'>
              <InputField
                id='firstName'
                type='text'
                label={t('complete-profile-form.first-name')}
                required
              />
              <InputField
                id='lastName'
                type='text'
                label={t('complete-profile-form.last-name')}
                required
              />
            </div>
            <InputField
              id='email'
              type='email'
              label={t('complete-profile-form.email')}
              required
              validate={(value) =>
                isEmail(value) || 'enter a valid email address'
              }
              onBlur={onBlurInput(email)}
            />
            <InternationalPhoneInput
              id='phone'
              label={t('pwless-auth.phone-number')}
              value={accountInfo.phoneNumber}
              unregisterOnUnmount
              shouldUnFocus
              initialFocus={false}
              disabled
            />
          </div>
        </div>
        <div className='footer'>
          <Button
            className='submitBtn'
            type='submit'
            disabled={
              submitting || !(isEmail(email || '') && firstName && lastName)
            }
            testId='create-account-button'
          >
            {t('complete-profile-form.create-account')}
          </Button>
          <div className='authDisclaimer' data-testid='authDisclaimer'>
            <AccountCreationLegalDisclaimer />
          </div>
          <div className='checkoutAsGuestFooter'>
            <div className='orSeparator'>
              <div className='separator' />
              <div>{t('pwless-auth.or')}</div>
              <div className='separator' />
            </div>
            <button
              data-testid='continue-as-guest-button'
              className='checkoutAsGuestButton'
              type='button' // don't submit the checkout form!
              onClick={() => {
                handleClose()
                saveValuesForCheckout?.(formMethods.getValues())
              }}
            >
              {checkingOut
                ? t('pwless-auth.checkout-as-guest')
                : t('complete-profile-form.continue-as-guest')}
            </button>
          </div>
        </div>
      </form>
    </FormProvider>
  )
}

const SuccessOrError = ({
  loading,
  success
}: {
  loading: boolean
  success: boolean
}) => {
  return (
    <div className='successOrErrorContainer'>
      <div className='successIndicatorContainer'>
        {loading ? (
          <Skeleton className='indicator loading' />
        ) : success ? (
          <CheckCircleSelectedIcon
            accessibility='decorative'
            className='indicator success'
          />
        ) : (
          <CloseCircleIcon
            accessibility='decorative'
            className='indicator error'
          />
        )}
      </div>
      <div className='title'>
        {loading ? (
          <Skeleton className='loadingTitle' />
        ) : success ? (
          t('complete-profile-form.account-created')
        ) : (
          t('complete-profile-form.account-creation-error')
        )}
      </div>
    </div>
  )
}
